import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import BugReportIcon from '@mui/icons-material/BugReport';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { beehiveTheme } from '../../styles/beehiveTheme';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(15, 0),
  backgroundColor: '#fafafa',
  position: 'relative',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '2.75rem',
  marginBottom: theme.spacing(4),
  color: beehiveTheme.primary.main,
  lineHeight: 1.2,
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem',
  },
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#4a5568',
  lineHeight: 1.9,
  marginBottom: theme.spacing(4),
}));

const StatBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 4),
  backgroundColor: beehiveTheme.primary.main,
  borderRadius: '12px',
  textAlign: 'center',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200px',
    height: '200px',
    background: `radial-gradient(circle, ${beehiveTheme.accent.main}20 0%, transparent 70%)`,
  },
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  fontSize: '4.5rem',
  fontWeight: 900,
  color: beehiveTheme.accent.main,
  lineHeight: 1,
  marginBottom: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    fontSize: '3.5rem',
  },
}));

const StatText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#e2e8f0',
  fontWeight: 500,
  lineHeight: 1.6,
  position: 'relative',
  zIndex: 1,
}));

const QuoteBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  borderLeft: `6px solid ${beehiveTheme.accent.main}`,
  backgroundColor: 'white',
  borderRadius: '0 8px 8px 0',
  marginTop: theme.spacing(8),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const ImpactGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: theme.spacing(3),
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(6),
}));

const ImpactItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: 'white',
  borderRadius: '8px',
  textAlign: 'center',
  border: `2px solid ${beehiveTheme.neutral.lightGray}`,
}));

export default function ProblemSection() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <SectionContainer id="problem">
      <Container maxWidth="lg">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <Box sx={{ maxWidth: '800px', margin: '0 auto', mb: 8 }}>
            <Title>
              Here's the problem: Varroa mites are killing bees, and we're detecting them too late.
            </Title>

            <Paragraph>
              A tiny parasitic mite—just 1.1mm across—has become the biggest threat to honey bee colonies worldwide. 
              These mites weaken bees, spread viruses, and can wipe out entire hives if left unchecked.
            </Paragraph>

            <Paragraph>
              The worst part? By the time beekeepers notice the problem using traditional methods, it's often too late. 
              The colony is already struggling, and the window for effective treatment has closed.
            </Paragraph>
          </Box>
        </motion.div>

        {/* Stats Grid */}
        <Grid container spacing={4} sx={{ mb: 10 }}>
          <Grid item xs={12} md={4}>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
              <StatBox>
                <StatNumber>30%</StatNumber>
                <StatText>of bee colonies lost annually, with Varroa as the leading cause</StatText>
              </StatBox>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={4}>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
              <StatBox>
                <StatNumber>3-4</StatNumber>
                <StatText>weeks for mite population to double under ideal conditions</StatText>
              </StatBox>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={4}>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
              <StatBox>
                <StatNumber>300</StatNumber>
                <StatText>bees sacrificed per test with traditional alcohol wash method</StatText>
              </StatBox>
            </motion.div>
          </Grid>
        </Grid>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: beehiveTheme.primary.main, mb: 4, textAlign: 'center' }}>
              Why current methods fail:
            </Typography>

            <ImpactGrid>
              <ImpactItem>
                <BugReportIcon sx={{ fontSize: '3rem', mb: 1, color: beehiveTheme.accent.main }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  Destructive Testing
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', color: '#4a5568', lineHeight: 1.6 }}>
                  Alcohol wash kills 300 bees per sample
                </Typography>
              </ImpactItem>

              <ImpactItem>
                <BarChartIcon sx={{ fontSize: '3rem', mb: 1, color: beehiveTheme.accent.main }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  High Variance
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', color: '#4a5568', lineHeight: 1.6 }}>
                  ±15-25% accuracy with sticky boards
                </Typography>
              </ImpactItem>

              <ImpactItem>
                <AccessTimeIcon sx={{ fontSize: '3rem', mb: 1, color: beehiveTheme.accent.main }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  Too Infrequent
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', color: '#4a5568', lineHeight: 1.6 }}>
                  Manual checks every few weeks
                </Typography>
              </ImpactItem>

              <ImpactItem>
                <WarningAmberIcon sx={{ fontSize: '3rem', mb: 1, color: beehiveTheme.accent.main }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  Reactive Only
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', color: '#4a5568', lineHeight: 1.6 }}>
                  Detects after damage occurs
                </Typography>
              </ImpactItem>
            </ImpactGrid>

            <Paragraph sx={{ mt: 4 }}>
              Varroa populations don't wait. They grow exponentially. A manageable infestation can become a crisis 
              in just a few weeks—faster than most inspection schedules can catch.
            </Paragraph>

            <QuoteBox>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: beehiveTheme.primary.main, lineHeight: 1.7, fontStyle: 'italic' }}>
                "The real challenge isn't detecting Varroa mites. It's detecting them early enough to actually do something about it."
              </Typography>
            </QuoteBox>
          </Box>
        </motion.div>
      </Container>
    </SectionContainer>
  );
}
